"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, PhoneCall, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface FaqItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}
const faqs: FaqItem[] = [{
  id: "permits-dekalb",
  question: "Do I need city permits for a bathroom remodel in Brookhaven, GA?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Yes. Any bathroom renovation involving structural modifications, layout reconfigurations, new plumbing branch lines, or electrical circuit upgrades requires permitting through the City of Brookhaven Community Development Department.
        </p>
        <p>
          Brookhaven Elevation Renovations handles the full submittal package, including architectural drawings, MEP schedules, and scheduled city inspections:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 marker:text-primary">
          <li><strong>Rough-in inspections:</strong> Framing, plumbing pressure test, electrical wiring, and mechanical venting before walls are closed.</li>
          <li><strong>Waterproofing inspection:</strong> Shower pan and wet area membrane review to verify code compliance.</li>
          <li><strong>Final inspection:</strong> Fixture operation, GFCI protection verification, and issuance of closed-out permit documentation.</li>
        </ul>
      </div>
}, {
  id: "living-in-home",
  question: "Can we remain living in our home during the bathroom renovation?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          In most cases, yes—provided you have access to a second operational bathroom elsewhere in your residence. For primary ensuite renovations, we establish strict containment protocols to keep your living quarters quiet, clean, and private.
        </p>
        <ul className="list-disc pl-5 space-y-1.5 marker:text-primary">
          <li>Zippered, negative-air plastic barriers at bedroom entries to trap drywall and tile dust.</li>
          <li>Heavy-duty floor runners along all access corridors and stairwells.</li>
          <li>HEPA scrubbers running during tile tear-out, slab cutting, and wall sanding.</li>
          <li>Daily broom-clean sweeps and end-of-day site vacuuming.</li>
        </ul>
        <p>
          If the project is a single-bathroom cottage where water must be shut off for extended periods, we schedule those tie-ins tightly so water is restored every evening.
        </p>
      </div>
}, {
  id: "project-timeline",
  question: "What is the typical timeline for a complete bathroom remodel?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          A standard Brookhaven bathroom remodel typically takes between 4 and 8 weeks of active site construction, depending on scale and complexity:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <h4 className="font-semibold text-foreground text-xs uppercase tracking-wide mb-1">Guest / Hall Bath (4–5 Weeks)</h4>
            <p className="text-xs text-muted-foreground">Demolition, plumbing relocation, tile surround, vanity installation, fixtures, paint, and trim.</p>
          </div>
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <h4 className="font-semibold text-foreground text-xs uppercase tracking-wide mb-1">Primary Spa Ensuite (6–8 Weeks)</h4>
            <p className="text-xs text-muted-foreground">Curbless showers, freestanding soaking tubs, heated radiant floors, custom dual vanities, and framed glass.</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground italic">
          *Note: All tile, plumbing trim, and custom cabinetry are ordered and verified in our staging warehouse before demo starts to avoid delays during construction.
        </p>
      </div>
}, {
  id: "change-orders",
  question: "How do you handle hidden issues and change orders during construction?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Older Brookhaven homes—especially mid-century ranches in Ashford Park or historic properties near Capital City Club—can conceal subfloor rot, galvanized supply pipes, or ungrounded wiring once tiles are stripped.
        </p>
        <p>
          Our change order process is transparent and zero-surprise:
        </p>
        <ol className="list-decimal pl-5 space-y-1.5 marker:text-primary">
          <li><strong>Immediate visual documentation:</strong> We pause work in that specific zone and share photos/video directly through your homeowner portal.</li>
          <li><strong>Written scope & pricing:</strong> You receive a clear description of the remedy with exact trade labor and materials costs before any additional work proceeds.</li>
          <li><strong>Digital authorization:</strong> You review and approve the adjustment electronically, keeping budget tracking clear at every milestone.</li>
        </ol>
      </div>
}, {
  id: "design-selections",
  question: "Do you assist with material selection and space planning?",
  answer: <div className="space-y-3 text-muted-foreground leading-relaxed">
        <p>
          Yes. Our pre-construction phase pairs you with an interior designer who produces detailed 3D elevations and helps select complementary surfaces:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 marker:text-primary">
          <li>Tile layouts: Large-format porcelain, Zellige accents, chevron patterns, and mosaic shower pans.</li>
          <li>Plumbing fixtures: Solid brass valves, thermostatic controls, body sprays, and freestanding tub fillers.</li>
          <li>Countertops & cabinetry: Custom white oak or painted vanity units paired with quartzite or quartz tops.</li>
          <li>Integrated lighting: Recessed wet-rated LED canisters, backlit vanity mirrors, and wall sconces.</li>
        </ul>
      </div>
}];
export default function BathroomRemodelFaq() {
  return <section id="bathroomremodelfaq" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Context, Visual Showcase & Direct Contact */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <HelpCircle className="size-4" />
                <span>Brookhaven Remodeling Guide</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl text-foreground font-semibold tracking-tight">
                Frequently asked questions about bathroom renovations
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed">
                Clear answers regarding permits, containment, build timelines, and municipal requirements for Brookhaven homeowners.
              </p>
            </div>

            {/* Realistic Project Photograph */}
            <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-sm">
              <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_master_bath_renovation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_master_bath_renovation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_master_bath_renovation.png-wvc-sizes" alt="Custom tiled walk-in shower with dual vanities and brass hardware in a renovated Brookhaven home" loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105" />
              </div>
              <div className="p-4 bg-card border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Ashford Park Ensuite Project</span>
                <span>Permitted & inspected via Brookhaven</span>
              </div>
            </div>

            {/* Quick Consultation Prompt Card */}
            <div className="rounded-xl border border-border bg-muted/40 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                  <PhoneCall className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-sm">Have a unique layout or question?</h3>
                  <p className="text-xs text-muted-foreground">Speak directly with our Brookhaven project directors.</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button asChild variant="default" size="sm" className="w-full sm:w-auto font-medium">
                  <Link to="/ContactRequestAnEstimatePage">
                    Schedule an in-home estimate
                    <ArrowRight className="size-3.5 ml-1.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                  <Link to="/OurProjectsPage">View bathroom gallery</Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Accordion Questions & Answers */}
          <div className="lg:col-span-7">
            <div className="rounded-xl border border-border bg-card shadow-sm p-6 sm:p-8">
              <Accordion type="single" collapsible defaultValue="permits-dekalb" className="w-full">
                {faqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border-b border-border/80 py-1 last:border-b-0">
                    <AccordionTrigger className="text-left font-serif text-lg font-medium text-foreground hover:text-primary transition-colors py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="pt-1 pb-5 text-sm text-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}